import * as React from "react";

import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import { useAnalyticsQueryContext } from "@v1/features/analytics/contexts/AnalyticsQueryContext";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { Globe } from "lucide-react";
import { pluralize } from "replo-utils/lib/pluralize";

type AnalyticsHostComboboxProps = {
  disabled?: boolean;
};

const AnalyticsUrlHostCombobox = ({ disabled }: AnalyticsHostComboboxProps) => {
  const { query, urlHostsObj, dispatchAnalyticsQuery } =
    useAnalyticsQueryContext();

  const logEvent = useLogAnalytics();

  const handleChange = (values: string[]) => {
    logEvent("analytics.urlHost.change", {
      urlHosts: values,
    });

    dispatchAnalyticsQuery({
      type: "urlHosts",
      payload: values,
    });
  };

  const workspaceUrlHostOptions = (urlHostsObj.data ?? []).map((host) => ({
    value: host,
    label: host,
  }));

  const selectedUrlHosts = query.urlHosts ?? [];

  const getDisplayTitle = () => {
    if (selectedUrlHosts.length === 0) {
      return "Select domains";
    }
    if (selectedUrlHosts.length === 1) {
      return selectedUrlHosts[0] ?? "";
    }
    return `${selectedUrlHosts.length} ${pluralize({ singular: "domain", plural: "domains", count: selectedUrlHosts.length })}`;
  };

  return urlHostsObj.isLoading ? (
    <Skeleton className="h-[24px] w-full" />
  ) : (
    <Combobox
      options={workspaceUrlHostOptions}
      value={selectedUrlHosts}
      onChange={handleChange}
      isMultiselect
      areOptionsSearchable={workspaceUrlHostOptions.length > 5}
      inputPlaceholder="Search domains..."
      trigger={
        <Combobox.SelectionButton
          isDisabled={disabled}
          title={getDisplayTitle()}
          isPlaceholder={selectedUrlHosts.length === 0}
          startEnhancer={<Globe className="h-4 w-4" />}
          layoutClassName="w-full min-w-0"
        />
      }
    />
  );
};

export default AnalyticsUrlHostCombobox;
